import React, { useEffect, useState } from 'react';
import api from '../api/api';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';

// SVG Icons
const CarIcon = () => (
    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
    </svg>
);

const EditIcon = () => (
    <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
);

const SaveIcon = () => (
    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
);

const CalendarIcon = () => (
    <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

const InsuranceIcon = () => (
    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
);

const OilIcon = () => (
    <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
    </svg>
);

const ChainIcon = () => (
    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
);

const SearchIcon = () => (
    <svg className="h-5 w-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
);

export default function EntretienPage() {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.language === 'ar';
    const [voitures, setVoitures] = useState([]);
    const [editing, setEditing] = useState(null);
    const [formData, setFormData] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('visite');
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const res = await api.get('/voitures');
                setVoitures(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Format date for input field (YYYY-MM-DD)
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    };

    const handleEdit = (voiture, section) => {
        setEditing({ id: voiture.id, section });
        
        // Format date fields in the object before setting form data
        const formattedVoiture = { ...voiture };
        Object.keys(formattedVoiture).forEach(key => {
            if (key.includes('date') && formattedVoiture[key]) {
                formattedVoiture[key] = formatDateForInput(formattedVoiture[key]);
            }
        });
        
        setFormData(formattedVoiture);
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const fieldsBySection = {
            visite: ['date_derniere_visite_tech', 'date_prochaine_visite_tech'],
            assurance: ['assurance_compagnie', 'numero_police_assurance', 'date_expiration_assurance'],
            vidange: ['date_derniere_vidange', 'kilometrage_derniere_vidange', 'kilometrage_prochaine_vidange'],
            chaine: ['date_remplacement_distribution', 'kilometrage_remplacement_distribution', 'kilometrage_prochain_distribution']
        };

        const payload = {};
        fieldsBySection[editing.section].forEach(field => {
            payload[field] = formData[field];
        });

        try {
            await api.put(`/voitures/${editing.id}`, payload);
            setEditing(null);
            const res = await api.get('/voitures');
            setVoitures(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const filteredVoitures = voitures.filter(voiture => {
        const matchesSearch =
            voiture.marque.toLowerCase().includes(searchTerm.toLowerCase()) ||
            voiture.matricule.toLowerCase().includes(searchTerm.toLowerCase());

        if (filter === 'all') return matchesSearch;
        if (filter === 'expired') {
            // Simple example - you'd need proper date comparison logic
            return matchesSearch && (
                new Date(voiture.date_prochaine_visite_tech) < new Date() ||
                new Date(voiture.date_expiration_assurance) < new Date()
            );
        }
        return matchesSearch;
    });

    const renderSection = (title, fields, sectionKey, icon) => (
        <div className={`p-6 rounded-2xl transition-all duration-300 ${activeTab === sectionKey ? 'block' : 'hidden'}`}>
            <div className="flex items-center mb-6">
                <div className="p-3 rounded-lg bg-blue-50 mr-4">
                    {icon}
                </div>
                <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className={`px-6 py-3 ${isRtl ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                {t('vehicle')}
                            </th>
                            {/* Added current mileage column for vidange and chaine sections */}
                            {(sectionKey === 'vidange' || sectionKey === 'chaine') && (
                                <th scope="col" className={`px-6 py-3 ${isRtl ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('current_mileage')}
                                </th>
                            )}
                            {fields.map(f => (
                                <th key={f} scope="col" className={`px-6 py-3 ${isRtl ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t(f)}
                                </th>
                            ))}
                            <th scope="col" className={`px-6 py-3 ${isRtl ? 'text-left' : 'text-right'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                {t('action')}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredVoitures.map(voiture => (
                            <tr key={voiture.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                            <CarIcon />
                                        </div>
                                        <div className={`${isRtl ? 'mr-4' : 'ml-4'}`}>
                                            <div className="text-sm font-medium text-gray-900">{voiture.marque}</div>
                                            <div className="text-sm text-gray-500">{voiture.matricule}</div>
                                        </div>
                                    </div>
                                </td>

                                {/* Added current mileage cell for vidange and chaine sections */}
                                {(sectionKey === 'vidange' || sectionKey === 'chaine') && (
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {voiture.kilometrage || 'N/A'}
                                        </div>
                                    </td>
                                )}

                                {fields.map(f => (
                                    <td key={f} className="px-6 py-4 whitespace-nowrap">
                                        {editing?.id === voiture.id && editing.section === sectionKey ? (
                                            <input
                                                type={f.includes('date') ? 'date' : 'text'}
                                                name={f}
                                                value={f.includes('date') ? formatDateForInput(formData[f]) : (formData[f] || '')}
                                                onChange={handleChange}
                                                className="border rounded-lg px-3 py-2 text-sm w-full focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        ) : (
                                            <div className="text-sm text-gray-900">
                                                {f.includes('date') ?
                                                    new Date(voiture[f]).toLocaleDateString() :
                                                    voiture[f]}
                                            </div>
                                        )}
                                    </td>
                                ))}
                                <td className={`px-6 py-4 whitespace-nowrap ${isRtl ? 'text-left' : 'text-right'} text-sm font-medium`}>
                                    {editing?.id === voiture.id && editing.section === sectionKey ? (
                                        <button
                                            onClick={handleSubmit}
                                            className="text-green-600 hover:text-green-800 bg-green-50 hover:bg-green-100 px-3 py-1 rounded-lg flex items-center space-x-1 transition-colors"
                                        >
                                            <SaveIcon />
                                            <span>{t('save')}</span>
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleEdit(voiture, sectionKey)}
                                            className="text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-lg flex items-center space-x-1 transition-colors"
                                        >
                                            <EditIcon />
                                            <span>{t('edit')}</span>
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
    return (
        <Sidebar>

            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 p-6" dir={isRtl ? 'rtl' : 'ltr'}>
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800 flex items-center">
                                <svg className={`w-8 h-8 text-blue-600 ${isRtl ? 'ml-3' : 'mr-3'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                                {t('maintenance_management')}
                            </h1>
                            <p className="text-gray-600 mt-2">{t('maintenance_desc')}</p>
                        </div>

                        <div className="mt-4 md:mt-0 w-full md:w-auto">
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <SearchIcon />
                                </div>
                                <input
                                    type="text"
                                    placeholder={t('search_vehicle')}
                                    className={`block w-full ${isRtl ? 'pr-10 pl-3' : 'pl-10 pr-3'} py-2 border border-gray-300 rounded-xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Filter and Tabs */}
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6">
                        <div className="border-b border-gray-200">
                            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center px-6 py-4 lg:py-0 gap-4 overflow-x-auto">
                                <nav className="flex -mb-px space-x-8">
                                    <button
                                        onClick={() => setActiveTab('visite')}
                                        className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${activeTab === 'visite' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                                    >
                                        <CalendarIcon />
                                        <span className={`${isRtl ? 'mr-2' : 'ml-2'}`}>{t('technical_visits')}</span>
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('assurance')}
                                        className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${activeTab === 'assurance' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                                    >
                                        <InsuranceIcon />
                                        <span className={`${isRtl ? 'mr-2' : 'ml-2'}`}>{t('insurances')}</span>
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('vidange')}
                                        className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${activeTab === 'vidange' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                                    >
                                        <OilIcon />
                                        <span className={`${isRtl ? 'mr-2' : 'ml-2'}`}>{t('oil_changes')}</span>
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('chaine')}
                                        className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${activeTab === 'chaine' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                                    >
                                        <ChainIcon />
                                        <span className={`${isRtl ? 'mr-2' : 'ml-2'}`}>{t('distributions')}</span>
                                    </button>
                                </nav>

                                <div className="flex items-center space-x-4">
                                    <select
                                        className="border rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                                        value={filter}
                                        onChange={(e) => setFilter(e.target.value)}
                                    >
                                        <option value="all">{t('all_vehicles_filter')}</option>
                                        <option value="expired">{t('expired_deadlines')}</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Loading State */}
                    {loading && (
                        <div className="flex justify-center items-center h-64 bg-white rounded-2xl shadow-xl">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                        </div>
                    )}

                    {/* Empty State */}
                    {!loading && voitures.length === 0 && (
                        <div className="text-center py-12 bg-white rounded-2xl shadow-xl">
                            <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h3 className="mt-4 text-xl font-medium text-gray-700">{t('no_vehicle_found_maintenance')}</h3>
                            <p className="mt-2 text-gray-500">{t('add_vehicles_desc_maintenance')}</p>
                        </div>
                    )}

                    {/* Content */}
                    {!loading && voitures.length > 0 && (
                        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                            {renderSection(
                                t('technical_visits'),
                                ['date_derniere_visite_tech', 'date_prochaine_visite_tech'],
                                'visite',
                                <CalendarIcon />
                            )}

                            {renderSection(
                                t('insurances'),
                                ['assurance_compagnie', 'numero_police_assurance', 'date_expiration_assurance'],
                                'assurance',
                                <InsuranceIcon />
                            )}

                            {renderSection(
                                t('oil_changes'),
                                ['date_derniere_vidange', 'kilometrage_derniere_vidange', 'kilometrage_prochaine_vidange'],
                                'vidange',
                                <OilIcon />
                            )}

                            {renderSection(
                                t('distributions'),
                                ['date_remplacement_distribution', 'kilometrage_remplacement_distribution', 'kilometrage_prochain_distribution'],
                                'chaine',
                                <ChainIcon />
                            )}
                        </div>
                    )}

                    {/* Stats Cards - Only visible on larger screens */}
                    <div className="hidden lg:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
                        <div className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-blue-500">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-500">{t('recorded_vehicles')}</p>
                                    <p className="text-2xl font-semibold text-gray-800">{voitures.length}</p>
                                </div>
                                <div className="p-3 rounded-full bg-blue-100">
                                    <CarIcon />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-purple-500">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-500">{t('upcoming_visits')}</p>
                                    <p className="text-2xl font-semibold text-gray-800">
                                        {voitures.filter(v => new Date(v.date_prochaine_visite_tech) > new Date()).length}
                                    </p>
                                </div>
                                <div className="p-3 rounded-full bg-purple-100">
                                    <CalendarIcon />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-blue-400">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-500">{t('expired_insurances')}</p>
                                    <p className="text-2xl font-semibold text-gray-800">
                                        {voitures.filter(v => new Date(v.date_expiration_assurance) < new Date()).length}
                                    </p>
                                </div>
                                <div className="p-3 rounded-full bg-blue-50">
                                    <InsuranceIcon />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-yellow-500">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-500">{t('required_oil_changes')}</p>
                                    <p className="text-2xl font-semibold text-gray-800">
                                        {voitures.filter(v => v.kilometrage_prochaine_vidange && v.kilometrage_prochaine_vidange < v.kilometrage).length}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Sidebar>

    );
}